<template>
  <div class="dashboard-title fl-wrap">
    <h3>AI Conversations History</h3>
  </div>
  
  <div
    class="profile-edit-container fl-wrap block_box"
    style="padding: 20px;"
  >
    <div
      v-if="loading"
      style="text-align: center; padding: 30px;"
    >
      <i
        class="fas fa-spinner fa-spin"
        style="font-size: 24px; color: #7f74c6;"
      />
      <p style="margin-top: 10px;">
        Loading conversations...
      </p>
    </div>
    
    <div
      v-else-if="conversations.length === 0"
      style="text-align: center; padding: 40px 20px; color: #666;"
    >
      <i
        class="fas fa-robot"
        style="font-size: 40px; color: #ccc; margin-bottom: 15px;"
      />
      <h4>No AI Conversations Found</h4>
      <p>Ask PetQueenAI a question to start your first conversation!</p>
    </div>
    
    <div
      v-else
      class="dashboard-list-box fl-wrap"
    >
      <div 
        v-for="convo in conversations" 
        :key="convo.id" 
        class="dashboard-list fl-wrap"
        style="display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #eee;"
      >
        <div class="dashboard-message">
          <div class="dashboard-message-text">
            <i
              class="fas fa-comments"
              style="color: #7f74c6; margin-right: 10px;"
            />
            <strong>{{ convo.title || 'Conversation' }}</strong>
            <p style="font-size: 12px; color: #888; margin-top: 5px;">
              {{ formatDate(convo.updatedAt) }} • {{ convo.messages?.length || 0 }} messages
            </p>
          </div>
        </div>
        <div class="dashboard-message-actions">
          <a 
            href="javascript:void(0)" 
            class="btn color-bg" 
            style="padding: 8px 15px; border-radius: 4px; color: white;"
            @click="loadChat(convo)"
          >
            <i class="fas fa-eye" /> View Chat
          </a>
        </div>
      </div>
      
      <div
        v-if="hasMore"
        style="text-align: center; margin-top: 20px;"
      >
        <button 
          class="btn color2-bg" 
          style="padding: 10px 20px; border-radius: 4px; color: white; border: none; cursor: pointer;" 
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? 'Loading...' : 'Load More' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { db } from '@/firebase/index.js';
import { collection, query, where, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const conversations = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const hasMore = ref(false);
let lastVisible = null;

const PAGE_SIZE = 10;

const fetchConversations = async (isLoadMore = false) => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    loading.value = false;
    return;
  }
  
  try {
    if (isLoadMore) loadingMore.value = true;
    else loading.value = true;
    
    let q = query(
      collection(db, 'ai_conversations'),
      where('userId', '==', user.uid),
      orderBy('updatedAt', 'desc'),
      limit(PAGE_SIZE)
    );
    
    if (isLoadMore && lastVisible) {
      q = query(
        collection(db, 'ai_conversations'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc'),
        startAfter(lastVisible),
        limit(PAGE_SIZE)
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    const newConvos = [];
    querySnapshot.forEach((doc) => {
      newConvos.push({ id: doc.id, ...doc.data() });
    });
    
    if (isLoadMore) {
      conversations.value = [...conversations.value, ...newConvos];
    } else {
      conversations.value = newConvos;
    }
    
    lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    hasMore.value = querySnapshot.docs.length === PAGE_SIZE;
    
  } catch (error) {
    console.error("Error fetching AI conversations:", error);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const loadMore = () => {
  if (hasMore.value && !loadingMore.value) {
    fetchConversations(true);
  }
};

const loadChat = (convo) => {
  // Dispatch a global event that ChatBotGemini.vue listens to
  const event = new CustomEvent('load-ai-chat', { 
    detail: { 
      id: convo.id, 
      messages: convo.messages 
    } 
  });
  window.dispatchEvent(event);
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString();
};

onMounted(() => {
  fetchConversations();
});
</script>

<style scoped>
.color-bg {
  background-color: #7f74c6;
}
.color-bg:hover {
  background-color: #6a60b8;
}
.color2-bg {
  background-color: #f8bbd0;
  color: #111 !important;
}
.color2-bg:hover {
  background-color: #f48fb1;
}
</style>
